import React, { useRef, useState } from "react";
import { CircleNotch, FileArrowUp, Sparkle, Calculator, FileText, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls = "w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]";
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const QLABELS = { q1: "Upto 15/6", q2: "16/6-15/9", q3: "16/9-15/12", q4: "16/12-15/3", q5: "16/3-31/3" };
const FYS = ["2023-24", "2024-25", "2025-26"];

export default function AutoFillIntake({ clients }) {
  const [busy, setBusy] = useState(false);
  const [ex, setEx] = useState(null);
  const [clientId, setClientId] = useState("");
  const [fy, setFy] = useState("2025-26");
  const ref = useRef(null);

  const analyze = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData(); files.forEach((f) => fd.append("files", f)); fd.append("kind", "ais");
      const r = await api.post("/tax/intake/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEx(r.data.extracted);
      const errs = (r.data.files || []).filter((f) => f.error);
      if (errs.length) toast.error(`${errs.length} file(s) failed — ${errs[0].error?.slice(0, 60)}`);
      else toast.success("Extracted — review the values, then apply");
    } catch (err) { toast.error(err?.response?.data?.detail || "Extraction failed (check LLM budget)"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const setNum = (k, v) => setEx((p) => ({ ...p, [k]: Number(v) }));
  const applyFin = async () => {
    if (!clientId) { toast.error("Select a client"); return; }
    setBusy(true);
    try { await api.post("/tax/intake/apply-financials", { client_id: clientId, fy, extracted: ex }); toast.success("Applied to Financials — check the Financials tab"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); } finally { setBusy(false); }
  };
  const applyItr = async () => {
    if (!clientId) { toast.error("Select a client"); return; }
    setBusy(true);
    try { const r = await api.post("/tax/intake/apply-itr", { client_id: clientId, fy, extracted: ex }); toast.success(`Applied to ITR — ${r.data.selection.form}, review in the ITR Engine tab`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); } finally { setBusy(false); }
  };

  const cg = ex?.capital_gains_detail || { stcg: {}, ltcg: {} };

  return (
    <div className="bg-white border border-[#F29A86]/30 rounded-2xl p-5 mb-6" data-testid="ais-autofill">
      <div className="flex items-center gap-2 mb-1"><Sparkle size={18} weight="duotone" className="text-[#F29A86]" /><p className="font-display font-semibold">AIS / TIS / 26AS Auto-fill</p></div>
      <p className="text-sm text-[#594F4D] mb-4">Upload AIS / TIS / Form 26AS (or bank statements). AI reads interest, dividend, TDS, bank balances and capital-gains (with the 5-period quarterly breakup). Review, then push straight into Financials and the ITR return.</p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-xs text-[#8C8381]">Client</label>
          <select data-testid="ais-client" value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none w-52"><option value="">Select client…</option>{clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.firm_name}</option>)}</select>
        </div>
        <div><label className="text-xs text-[#8C8381]">F.Y.</label>
          <select data-testid="ais-fy" value={fy} onChange={(e) => setFy(e.target.value)} className="mt-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none">{FYS.map((f) => <option key={f}>{f}</option>)}</select>
        </div>
        <input ref={ref} type="file" multiple className="hidden" onChange={analyze} accept="image/*,.pdf,.csv,.xlsx,.txt,.json,.docx" />
        <button data-testid="ais-upload-btn" onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-2 bg-[#2A2625] text-white font-medium rounded-full px-5 py-2.5 text-sm">{busy ? <CircleNotch size={16} className="animate-spin" /> : <FileArrowUp size={16} weight="bold" />} Upload & analyze</button>
      </div>

      {ex && (
        <div className="grid gap-4" data-testid="ais-review">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["interest_income", "Interest income"], ["savings_bank_interest", "Savings bank interest"], ["dividend_income", "Dividend income"], ["total_tds", "Total TDS"]].map(([k, l]) => (
              <div key={k}><label className="text-xs text-[#8C8381]">{l}</label><input data-testid={`ais-${k}`} type="number" value={ex[k] || 0} onChange={(e) => setNum(k, e.target.value)} className={inputCls} /></div>
            ))}
          </div>

          {(ex.bank_closing_balances?.length > 0) && (
            <div><p className="text-xs font-semibold text-[#8C8381] uppercase tracking-wide mb-1">Bank closing balances → Balance In Bank ({money(ex.bank_total)})</p>
              <div className="flex flex-wrap gap-2">{ex.bank_closing_balances.map((b, i) => <span key={i} className="text-xs bg-[#FCFAF8] border border-[#2A2625]/10 rounded-lg px-2 py-1">{b.bank || "Bank"}: {money(b.balance)}</span>)}</div>
            </div>
          )}

          {/* Capital gains breakup */}
          <div>
            <p className="text-xs font-semibold text-[#8C8381] uppercase tracking-wide mb-2">Capital gains — quarterly accrual (for 234C)</p>
            <div className="overflow-x-auto bg-[#FCFAF8] rounded-xl border border-[#2A2625]/10">
              <table className="w-full text-xs" data-testid="ais-cg-table">
                <thead className="text-[#8C8381]"><tr>{["Type", "Consideration", "Acquisition", "Gain", ...Object.values(QLABELS)].map((h) => <th key={h} className="text-left px-2 py-2 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {["stcg", "ltcg"].map((t) => { const b = cg[t] || {}; const q = b.quarters || {}; return (
                    <tr key={t} className="border-t border-[#2A2625]/8">
                      <td className="px-2 py-1.5 font-medium">{t.toUpperCase()}</td>
                      <td className="px-2 py-1.5">{money(b.total_consideration)}</td>
                      <td className="px-2 py-1.5">{money(b.total_acquisition)}</td>
                      <td className="px-2 py-1.5 font-semibold">{money(b.total_gain)}</td>
                      {["q1", "q2", "q3", "q4", "q5"].map((qk) => <td key={qk} className="px-2 py-1.5">{money(q[qk])}</td>)}
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            {(cg.stcg?.items?.length > 0 || cg.ltcg?.items?.length > 0) && (
              <p className="text-[11px] text-[#8C8381] mt-1">{(cg.stcg?.items?.length || 0) + (cg.ltcg?.items?.length || 0)} transactions detected and bucketed by sale date.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button data-testid="ais-apply-fin" onClick={applyFin} disabled={busy} className="flex items-center gap-2 bg-[#FFB8A5] text-[#2A2625] font-medium rounded-full px-5 py-2.5 text-sm"><Calculator size={15} /> Apply to Financials</button>
            <button data-testid="ais-apply-itr" onClick={applyItr} disabled={busy} className="flex items-center gap-2 bg-[#A3C2A4]/25 text-[#3c663d] font-medium rounded-full px-5 py-2.5 text-sm"><FileText size={15} /> Apply to ITR return</button>
            <span className="flex items-center gap-1 text-xs text-[#8C8381]"><CheckCircle size={14} weight="fill" className="text-[#A3C2A4]" /> Values are editable above before you apply</span>
          </div>
        </div>
      )}
    </div>
  );
}
